import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mail, Phone, Building, User, FileText, ShoppingCart, Truck, Users, Bell, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const STATUS_PILL = {
  draft:      'bg-gray-200 text-gray-700',
  submitted:  'bg-blue-100 text-blue-700',
  pending:    'bg-blue-100 text-blue-700',
  invoiced:   'bg-amber-100 text-amber-800',
  paid:       'bg-emerald-100 text-emerald-700',
  dispatched: 'bg-purple-100 text-purple-700',
  delivered:  'bg-green-200 text-green-800',
  cancelled:  'bg-red-100 text-red-700',
};

const fmt = (s) => s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function Pill({ status }) {
  if (!status) return null;
  return <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-full ${STATUS_PILL[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-[#006400]" />
        <h2 className="font-bold text-lg text-[#27292E]">{title}</h2>
        <span className="ml-auto text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{count}</span>
      </div>
      {children}
    </Card>
  );
}

function RowList({ rows, render, empty = 'Nothing yet.' }) {
  if (!rows?.length) return <p className="text-sm text-gray-500">{empty}</p>;
  return <div className="divide-y divide-gray-100">{rows.map(render)}</div>;
}

export default function InstallerSnapshot() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/staff/installer-snapshot/${encodeURIComponent(code)}`, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(formatApiError(err) || `Lookup failed (${res.status})`);
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        toast.error(e.message || 'Lookup failed');
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading installer…</div>;
  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-gray-700">No installer matched <span className="font-mono font-bold">{code}</span>.</p>
      <Button onClick={() => navigate('/')} variant="outline">Back to portal</Button>
    </div>
  );

  const u = data.user;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b-4 border-[#006400]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button onClick={() => navigate(-1)} variant="outline" className="border-2 border-[#006400] text-[#006400]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">READ-ONLY VIEW</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Card className="p-5 bg-white">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#27292E]">{u.name || u.email}</h1>
                <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-full bg-gray-200 text-gray-700">{u.role || 'installer'}</span>
              </div>
              {u.company_name && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1"><Building className="w-3.5 h-3.5" /> {u.company_name}</p>}
              <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1"><Mail className="w-3.5 h-3.5" /> {u.email}</p>
              {u.phone && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" /> {u.phone}</p>}
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-right">
              <p className="text-[10px] uppercase tracking-wide font-bold text-gray-500">Account code</p>
              <p className="font-mono text-2xl font-bold text-[#006400]" data-testid="installer-account-code">{u.account_code || '—'}</p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Leads" icon={Users} count={data.leads.length}>
            <RowList
              rows={data.leads}
              empty="No leads on this account."
              render={(l) => (
                <div key={l.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{l.customer_name || '—'}</p>
                    <p className="text-xs text-gray-500 truncate">{l.service_type} · {l.customer_phone || l.customer_email || ''}</p>
                  </div>
                  <Pill status={l.status} />
                </div>
              )}
            />
          </Section>

          <Section title="ProBound — Material Quotes" icon={ShoppingCart} count={data.probound_quotes.length}>
            <RowList
              rows={data.probound_quotes}
              empty="No material quotes."
              render={(q) => (
                <div key={q.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-sm">{q.reference || q.id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-500 truncate">{q.customer_details?.name || ''} · {fmt(q.created_at)}</p>
                  </div>
                  <Pill status={q.status} />
                </div>
              )}
            />
          </Section>

          <Section title="ProDecor Orders" icon={Truck} count={data.prodecor_orders.length}>
            <RowList
              rows={data.prodecor_orders}
              empty="No ProDecor orders."
              render={(o) => (
                <div key={o.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-sm">{o.reference || o.id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-500 truncate">{o.customer_reference || ''} · £{o.total || 0} · {fmt(o.created_at)}</p>
                  </div>
                  <Pill status={o.status} />
                </div>
              )}
            />
          </Section>

          <Section title="ProEpoxy Orders" icon={Truck} count={data.proepoxy_orders.length}>
            <RowList
              rows={data.proepoxy_orders}
              empty="No ProEpoxy orders."
              render={(o) => (
                <div key={o.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-sm">{o.reference || o.id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-500 truncate">{o.customer_reference || ''} · £{o.total || 0} · {fmt(o.created_at)}</p>
                  </div>
                  <Pill status={o.status} />
                </div>
              )}
            />
          </Section>

          <Section title="Project Quotes" icon={FileText} count={data.project_quotes.length}>
            <RowList
              rows={data.project_quotes}
              empty="No customer-facing project quotes."
              render={(q) => (
                <div key={q.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-sm">{q.reference || q.id?.slice(0, 8)}</p>
                    <p className="text-xs text-gray-500 truncate">{q.customer_name || ''} · {fmt(q.created_at)}</p>
                  </div>
                  <Pill status={q.status} />
                </div>
              )}
            />
          </Section>

          <Section title="Unread Notifications" icon={Bell} count={data.unread_notifications.length}>
            <RowList
              rows={data.unread_notifications}
              empty="No unread notifications."
              render={(n) => (
                <div key={n.id} className="py-3">
                  <p className="font-semibold text-sm truncate">{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{n.type} · {fmt(n.created_at)}</p>
                </div>
              )}
            />
          </Section>
        </div>
      </main>
    </div>
  );
}
